// Performance monitoring and optimization utilities

import { logger } from './logger';

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  context?: string;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeObservers();
    this.startPerformanceTracking();
  }

  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        const navObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('page_load_time', navEntry.loadEventEnd - navEntry.fetchStart, 'Navigation');
              this.recordMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.fetchStart, 'Navigation');
              this.recordMetric('first_paint', navEntry.responseEnd - navEntry.fetchStart, 'Navigation');
            }
          }
        });
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navObserver);
      } catch (error) {
        logger.warn('Failed to initialize navigation observer', 'Performance', { error });
      }

      // Observe resource timing
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'resource') {
              const resourceEntry = entry as PerformanceResourceTiming;
              
              // Track slow resources
              const loadTime = resourceEntry.responseEnd - resourceEntry.fetchStart;
              if (loadTime > 1000) { // Resources taking more than 1 second
                this.recordMetric('slow_resource_load', loadTime, `Resource: ${resourceEntry.name}`);
              }
            }
          }
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      } catch (error) {
        logger.warn('Failed to initialize resource observer', 'Performance', { error });
      }

      // Observe largest contentful paint
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'largest-contentful-paint') {
              this.recordMetric('largest_contentful_paint', entry.startTime, 'LCP');
            }
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
      } catch (error) {
        logger.warn('Failed to initialize LCP observer', 'Performance', { error });
      }

      // Observe first input delay
      try {
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'first-input') {
              const fidEntry = entry as PerformanceEventTiming;
              this.recordMetric('first_input_delay', fidEntry.processingStart - fidEntry.startTime, 'FID');
            }
          }
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);
      } catch (error) {
        logger.warn('Failed to initialize FID observer', 'Performance', { error });
      }
    }
  }

  private startPerformanceTracking() {
    // Track memory usage
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        this.recordMetric('memory_used', memory.usedJSHeapSize, 'Memory');
        this.recordMetric('memory_total', memory.totalJSHeapSize, 'Memory');
        
        // Warn if memory usage is high
        const memoryUsagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        if (memoryUsagePercent > 80) {
          logger.warn(`High memory usage: ${memoryUsagePercent.toFixed(2)}%`, 'Performance');
        }
      }, 30000); // Check every 30 seconds
    }

    // Track frame rate
    let lastTime = performance.now();
    let frameCount = 0;
    
    const trackFPS = (currentTime: number) => {
      frameCount++;
      
      if (currentTime - lastTime >= 1000) {
        this.recordMetric('fps', frameCount, 'Animation');
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(trackFPS);
    };
    
    requestAnimationFrame(trackFPS);
  }

  recordMetric(name: string, value: number, context?: string) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      context
    };

    this.metrics.push(metric);

    // Keep only last 1000 metrics
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }

    // Log performance issues
    if (this.isPerformanceIssue(name, value)) {
      logger.warn(`Performance issue detected: ${name} = ${value}ms`, 'Performance', { context });
    }
  }

  private isPerformanceIssue(name: string, value: number): boolean {
    const thresholds: Record<string, number> = {
      'page_load_time': 3000,
      'dom_content_loaded': 2000,
      'largest_contentful_paint': 2500,
      'first_input_delay': 100,
      'slow_resource_load': 2000,
      'api_response_time': 1000
    };

    return thresholds[name] ? value > thresholds[name] : false;
  }

  getMetrics(name?: string): PerformanceMetric[] {
    if (name) {
      return this.metrics.filter(metric => metric.name === name);
    }
    return [...this.metrics];
  }

  getAverageMetric(name: string, timeWindow?: number): number {
    const now = Date.now();
    const windowStart = timeWindow ? now - timeWindow : 0;
    
    const relevantMetrics = this.metrics.filter(
      metric => metric.name === name && metric.timestamp > windowStart
    );

    if (relevantMetrics.length === 0) return 0;

    const sum = relevantMetrics.reduce((acc, metric) => acc + metric.value, 0);
    return sum / relevantMetrics.length;
  }

  exportMetrics(): string {
    return JSON.stringify(this.metrics, null, 2);
  }

  clearMetrics() {
    this.metrics = [];
  }

  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Image optimization utilities
class ImageOptimizer {
  static async compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  static generateThumbnail(file: File, size: number = 150): Promise<string> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = size;
        canvas.height = size;

        // Calculate crop area for square thumbnail
        const minDimension = Math.min(img.width, img.height);
        const x = (img.width - minDimension) / 2;
        const y = (img.height - minDimension) / 2;

        ctx?.drawImage(img, x, y, minDimension, minDimension, 0, 0, size, size);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };

      img.onerror = () => reject(new Error('Failed to generate thumbnail'));
      img.src = URL.createObjectURL(file);
    });
  }
}

// API optimization utilities
export class APIOptimizer {
  private static cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();

  static async cachedFetch(url: string, options: RequestInit = {}, ttl: number = 300000): Promise<Response> {
    const cacheKey = `${url}_${JSON.stringify(options)}`;
    const cached = this.cache.get(cacheKey);

    // Return cached response if valid
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      logger.debug(`Cache hit for ${url}`, 'APIOptimizer');
      return new Response(JSON.stringify(cached.data), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    // Make actual request
    const startTime = performance.now();
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Cache-Control': 'no-cache'
        }
      });

      const endTime = performance.now();
      performanceMonitor.recordMetric('api_response_time', endTime - startTime, `API: ${url}`);

      // Cache successful responses
      if (response.ok && options.method !== 'POST') {
        const data = await response.clone().json();
        this.cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          ttl
        });
      }

      return response;
    } catch (error) {
      const endTime = performance.now();
      performanceMonitor.recordMetric('api_error_time', endTime - startTime, `API Error: ${url}`);
      throw error;
    }
  }

  static clearCache(pattern?: string) {
    if (pattern) {
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  static getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Bundle size analyzer
class BundleAnalyzer {
  static analyzeChunks() {
    if ('performance' in window && 'getEntriesByType' in performance) {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      
      const jsChunks = resources.filter(resource => 
        resource.name.includes('.js') && resource.name.includes('assets')
      );

      const cssChunks = resources.filter(resource => 
        resource.name.includes('.css') && resource.name.includes('assets')
      );

      logger.info('Bundle Analysis', 'Performance', {
        jsChunks: jsChunks.length,
        cssChunks: cssChunks.length,
        totalJSSize: jsChunks.reduce((sum, chunk) => sum + (chunk.transferSize || 0), 0),
        totalCSSSize: cssChunks.reduce((sum, chunk) => sum + (chunk.transferSize || 0), 0)
      });
    }
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Initialize performance monitoring
export const initializePerformanceMonitoring = () => {
  // Analyze bundle on load
  window.addEventListener('load', () => {
    setTimeout(() => {
      BundleAnalyzer.analyzeChunks();
    }, 1000);
  });

  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      performanceMonitor.recordMetric('page_hidden', Date.now(), 'Visibility');
    } else {
      performanceMonitor.recordMetric('page_visible', Date.now(), 'Visibility');
    }
  });

  logger.info('Performance monitoring initialized', 'Performance');
};